'use client'

import { usePathname } from 'next/navigation'
import { createContext, useContext, useEffect } from 'react'
import { useCallback, useMemo, useRef, useState } from 'react'

import {
  getElementStylePropertyValue,
  setDefaultMetaThemeColor,
  setMetaThemeColor,
} from '@pearlconvert/ui'
import { SlideIn } from '@pearlconvert/ui/components/slide-in'
import { useDisabledPageScroll } from '@pearlconvert/ui/providers/disabled-page-scroll/disabled-page-scroll.context'
import {
  Modal,
  ModalsProviderProps,
  ModalsProviderValueProps,
} from '@pearlconvert/ui/providers/modals/types'

const SLIDE_IN_THEME_COLOR = '--slidein__header-background-color'

const initialValue: ModalsProviderValueProps = {
  closeModal: () => {},
  openModal: () => {},
  addModal: () => {},
  updateModal: () => {},
  isOpen: () => false,
}

export const ModalsContext =
  createContext<ModalsProviderValueProps>(initialValue)
const { Provider } = ModalsContext

export const ModalsProvider = ({
  children,
  initialModals = [],
}: ModalsProviderProps) => {
  const [modals, setModals] = useState<Modal[]>(initialModals)
  const [openModals, setOpenModals] = useState<string[]>([])

  const { toggleDisabledPageScroll } = useDisabledPageScroll()
  const toastContainerRef = useRef<HTMLDivElement>(
    null
  ) as React.RefObject<HTMLDivElement>

  const pathname = usePathname()

  const addModal: ModalsProviderValueProps['addModal'] = useCallback(
    ({ key, Component, props, hash }) => {
      setModals((state) => {
        const existingModal = state.find((modal) => modal.key === key)

        if (!existingModal) {
          state = [...state, { key, Component, props, hash }]
        } else {
          const newModal = { key, Component, props, hash }
          state = state.map((m) => (m.key === key ? newModal : m))
        }
        return state
      })
    },
    []
  )

  const openModal: ModalsProviderValueProps['openModal'] = useCallback(
    (key, cb?: () => void) => {
      toggleDisabledPageScroll(true)
      setOpenModals((state) => (state.includes(key) ? state : [...state, key]))

      setMetaThemeColor(
        getElementStylePropertyValue(
          document.documentElement,
          SLIDE_IN_THEME_COLOR
        )
      )

      cb?.()
    },
    [toggleDisabledPageScroll]
  )

  const closeModal: ModalsProviderValueProps['closeModal'] = useCallback(
    (key?: string, cb?: () => void) => {
      if (!key) {
        toggleDisabledPageScroll(false)
        setOpenModals([])

        setDefaultMetaThemeColor()
      } else {
        setOpenModals((state) => state.filter((modalKey) => modalKey !== key))
        if (!openModals.length) {
          toggleDisabledPageScroll(false)

          setDefaultMetaThemeColor()
        }
      }

      cb?.()
    },
    [toggleDisabledPageScroll, openModals.length]
  )

  const updateModal: ModalsProviderValueProps['updateModal'] = useCallback(
    (key, newProps) => {
      setModals((state) =>
        state.map((modal) => {
          if (modal.key === key) {
            if (typeof newProps === 'object' && newProps !== null) {
              const updatedComponent = {
                ...modal.Component,
                props: { ...modal.Component.props, ...newProps },
              }
              return { ...modal, Component: updatedComponent }
            }
          }
          return modal
        })
      )
    },
    []
  )

  const isOpen = useCallback(
    (key: string): boolean => {
      return openModals.includes(key)
    },
    [openModals]
  )

  const content = useMemo(() => {
    return modals?.map((modal) => {
      const isNotFirstModal = openModals.indexOf(modal.key) > 0

      return (
        <SlideIn
          key={modal.key}
          modalId={modal.key}
          {...modal.props}
          isOpen={openModals.includes(modal.key)}
          onClose={() => closeModal(modal.key, modal.props?.onClose)}
          onOpen={() => openModal(modal.key, modal.props?.onOpen)}
          onCloseAll={() => closeModal()}
          hasBackButton={openModals.length > 1 && isNotFirstModal}
          hash={modal.hash}
          toastContainerRef={toastContainerRef}
          openModals={openModals}
        >
          {modal.Component}
        </SlideIn>
      )
    })
  }, [closeModal, modals, openModal, openModals])

  useEffect(() => {
    if (openModals.length) {
      closeModal()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  const value = useMemo(() => {
    return {
      addModal,
      openModal,
      closeModal,
      updateModal,
      isOpen,
      toastContainerRef,
    }
  }, [addModal, openModal, closeModal, updateModal, isOpen])

  return (
    <Provider value={value}>
      {content}
      {children}
    </Provider>
  )
}

export const useModals = () => useContext(ModalsContext)
